import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { StreamingText } from "./StreamingText";
import { isExternalHttpUrl, safeUrlTransform } from "./urlPolicy";

export type MarkdownRenderState = "streaming" | "complete";

export type MarkdownMessageProps = {
  content: string;
  state?: MarkdownRenderState;
  className?: string;
};

function joinClassNames(...values: Array<string | undefined>): string {
  return values.filter(Boolean).join(" ");
}

/**
 * 渲染 Agent 返回内容。
 *
 * - 流式阶段只显示纯文本，避免未闭合 Markdown 反复重建 DOM；
 * - 完成态启用 CommonMark + GFM；
 * - 原始 HTML 永远禁用；
 * - URL 先经过显式协议白名单，危险链接降级为普通文字；
 * - 外链隔离 opener 和 referrer，图片延迟加载且不发送 referrer。
 */
export function MarkdownMessage({ className, content, state = "complete" }: MarkdownMessageProps) {
  if (state === "streaming") {
    return <StreamingText content={content} {...(className ? { className } : {})} />;
  }

  return (
    <div className={joinClassNames("herskin-markdown", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        urlTransform={safeUrlTransform}
        components={{
          a: ({ node, href, children, ...props }) => {
            void node;
            if (!href) {
              return <span className="herskin-markdown__blocked-link">{children}</span>;
            }

            const isExternal = isExternalHttpUrl(href);
            return (
              <a
                {...props}
                href={href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                referrerPolicy={isExternal ? "no-referrer" : undefined}
              >
                {children}
                {isExternal ? <span className="herskin-sr-only">（在新窗口打开）</span> : null}
              </a>
            );
          },
          code: ({ node, className: codeClassName, children, ...props }) => {
            void node;
            const language = codeClassName?.replace(/^language-/, "");
            return (
              <code {...props} className={codeClassName} data-language={language || undefined}>
                {children}
              </code>
            );
          },
          table: ({ node, children, ...props }) => {
            void node;
            return (
              <div
                className="herskin-markdown__table-scroll"
                tabIndex={0}
                aria-label="可横向滚动的表格"
              >
                <table {...props}>{children}</table>
              </div>
            );
          },
          img: ({ node, alt, src, ...props }) => {
            void node;
            if (!src) {
              const blockedImageLabel = alt ? `图片地址已拦截：${alt}` : "图片地址已拦截";
              return (
                <span
                  className="herskin-markdown__blocked-image"
                  role="img"
                  aria-label={blockedImageLabel}
                >
                  {blockedImageLabel}
                </span>
              );
            }

            return (
              // Agent 返回的远程图片不得携带 HerSkin 当前页面 Referrer。
              <img
                {...props}
                src={src}
                alt={alt ?? "Agent 返回的图片"}
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
              />
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
